import { type ChurchFormData } from "../types";

interface Props {
  data: ChurchFormData;
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-stone-50 last:border-0">
      <span className="text-xs font-bold text-stone-400 w-28 flex-shrink-0 pt-0.5 uppercase tracking-wide" style={{ fontFamily: "'Nunito', sans-serif" }}>
        {label}
      </span>
      <span className="text-sm text-stone-700 flex-1" style={{ fontFamily: "'Nunito', sans-serif" }}>{value}</span>
    </div>
  );
}

export default function StepReview({ data }: Props) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-stone-400 leading-relaxed" style={{ fontFamily: "'Nunito', sans-serif" }}>
        Vérifiez toutes les informations avant d&apos;ajouter l&apos;église à la liste.
      </p>

      {/* Image preview */}
      {data.image && (
        <div className="w-full h-40 rounded-2xl overflow-hidden">
          <img src={data.image} alt={data.name} className="w-full h-full object-cover object-top" />
        </div>
      )}

      {/* Infos de base */}
      <div className="bg-white rounded-2xl px-4 py-1 overflow-hidden">
        <p className="text-xs font-bold text-stone-400 uppercase tracking-wider pt-3 pb-1" style={{ fontFamily: "'Nunito', sans-serif" }}>
          Informations générales
        </p>
        <ReviewRow label="Nom" value={data.name} />
        <ReviewRow label="Diocèse" value={data.diocese} />
        <ReviewRow label="Ville" value={data.city} />
        <ReviewRow label="Adresse" value={data.address} />
        <ReviewRow label="Vidéo" value={data.videoFile?.name || ""} />
        {data.description && (
          <div className="py-2.5 border-b border-stone-50">
            <span className="text-xs font-bold text-stone-400 uppercase tracking-wide block mb-1" style={{ fontFamily: "'Nunito', sans-serif" }}>
              Description
            </span>
            <p className="text-sm text-stone-600 leading-relaxed" style={{ fontFamily: "'Nunito', sans-serif" }}>{data.description}</p>
          </div>
        )}
      </div>

      {/* Messes */}
      {data.massSchedule.some((s) => s.day && s.time) && (
        <div className="bg-white rounded-2xl px-4 py-1 overflow-hidden">
          <p className="text-xs font-bold text-amber-600 uppercase tracking-wider pt-3 pb-1" style={{ fontFamily: "'Nunito', sans-serif" }}>
            Horaires des messes
          </p>
          {data.massSchedule.filter((s) => s.day && s.time).map((s, i) => (
            <div key={i} className="flex items-center justify-between py-2.5 border-b border-stone-50 last:border-0">
              <span className="text-sm text-stone-600" style={{ fontFamily: "'Nunito', sans-serif" }}>{s.day}</span>
              <span className="text-sm font-bold text-amber-700 bg-amber-50 px-3 py-1 rounded-xl" style={{ fontFamily: "'Nunito', sans-serif" }}>{s.time}</span>
            </div>
          ))}
        </div>
      )}

      {/* Confessions */}
      {data.confessionSchedule.some((s) => s.day && s.time) && (
        <div className="bg-white rounded-2xl px-4 py-1 overflow-hidden">
          <p className="text-xs font-bold uppercase tracking-wider pt-3 pb-1" style={{ fontFamily: "'Nunito', sans-serif", color: "#0D1B5E" }}>
            Confessions
          </p>
          {data.confessionSchedule.filter((s) => s.day && s.time).map((s, i) => (
            <div key={i} className="flex items-center justify-between py-2.5 border-b border-stone-50 last:border-0">
              <span className="text-sm text-stone-600" style={{ fontFamily: "'Nunito', sans-serif" }}>{s.day}</span>
              <span className="text-sm font-bold px-3 py-1 rounded-xl" style={{ fontFamily: "'Nunito', sans-serif", color: "#0D1B5E", background: "rgba(13,27,62,0.07)" }}>{s.time}</span>
            </div>
          ))}
        </div>
      )}

      {/* Heures de bureau */}
      {data.officeHours.some((s) => s.day && s.time) && (
        <div className="bg-white rounded-2xl px-4 py-1 overflow-hidden">
          <p className="text-xs font-bold text-stone-500 uppercase tracking-wider pt-3 pb-1" style={{ fontFamily: "'Nunito', sans-serif" }}>
            Heures de bureau
          </p>
          {data.officeHours.filter((s) => s.day && s.time).map((s, i) => (
            <div key={i} className="flex items-center justify-between py-2.5 border-b border-stone-50 last:border-0">
              <span className="text-sm text-stone-600" style={{ fontFamily: "'Nunito', sans-serif" }}>{s.day}</span>
              <span className="text-sm font-bold text-stone-700 bg-stone-50 px-3 py-1 rounded-xl" style={{ fontFamily: "'Nunito', sans-serif" }}>{s.time}</span>
            </div>
          ))}
        </div>
      )}

      {/* Équipe */}
      {data.team.some((m) => m.name) && (
        <div className="bg-white rounded-2xl overflow-hidden">
          <p className="text-xs font-bold text-stone-400 uppercase tracking-wider px-4 pt-3 pb-2" style={{ fontFamily: "'Nunito', sans-serif" }}>
            Équipe presbytérale
          </p>
          {data.team.filter((m) => m.name).map((m, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-t border-stone-50">
              <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-stone-100 flex items-center justify-center">
                {m.image ? (
                  <img src={m.image} alt={m.name} className="w-full h-full object-cover object-top" />
                ) : (
                  <i className="ri-user-line text-stone-300 text-base" />
                )}
              </div>
              <div>
                <p className="text-sm font-bold text-stone-700" style={{ fontFamily: "'Nunito', sans-serif" }}>{m.name}</p>
                <span className="text-xs text-stone-400" style={{ fontFamily: "'Nunito', sans-serif" }}>{m.role}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Contacts */}
      {data.contacts.some((c) => c.value) && (
        <div className="bg-white rounded-2xl px-4 py-1 overflow-hidden">
          <p className="text-xs font-bold text-stone-400 uppercase tracking-wider pt-3 pb-1" style={{ fontFamily: "'Nunito', sans-serif" }}>
            Contacts
          </p>
          {data.contacts.filter((c) => c.value).map((c, i) => (
            <div key={i} className="flex items-center justify-between py-2.5 border-b border-stone-50 last:border-0">
              <span className="text-xs text-stone-400 font-semibold" style={{ fontFamily: "'Nunito', sans-serif" }}>{c.type}</span>
              <span className="text-sm font-bold text-stone-700" style={{ fontFamily: "'Nunito', sans-serif" }}>{c.value}</span>
            </div>
          ))}
        </div>
      )}

      {data.video && (
        <div className="bg-white rounded-2xl px-4 py-4 overflow-hidden">
          <p className="text-xs font-bold text-stone-400 uppercase tracking-wider pb-3" style={{ fontFamily: "'Nunito', sans-serif" }}>
            Aperçu vidéo
          </p>
          <div className="overflow-hidden rounded-xl bg-black">
            <video src={data.video} controls className="w-full max-h-72 object-cover" />
          </div>
        </div>
      )}
    </div>
  );
}
